import { BMS_SHORT_NAME } from "../../../constant/assistanceProgram";
import { TEMPLATE_NAMES } from "../ApplicationFormEditor/templateNames";
import { TEVA_COPAY_CARD_PID, TEVA_SUPPORT_PROGRAM_PID } from "./shared/expressEnrollProgramUtils";

/**
 * FIELD_VALUE_OVERRIDES
 *
 * This object defines static field-level overrides per templateName (vendor).
 * It's used to override or patch assistance program fields before any rendering/masking logic is applied.
 *
 * Use cases:
 * - Override values without changing the source-of-truth (AP DB)
 * - Enforcing template-specific adjustments (e.g., hiding a field by nullifying it)
 * - Providing values that don’t come from the backend but should always show in the UI
 *
 * Notes:
 * - Any field listed here will override the corresponding value from the original API response.
 * - Can be used in conjunction with FIELD_MASKING_CONFIG for full control.
 */

const regeneronCopayOverrides = {
  requiredDiagnosis: "aNSCLC, aBCC, aCSCC, High-Risk CSCC",
  assetId: "US.LIB.26.01.0097 02/26",
  APProvider: "Regeneron",
  displayName: "LIBTAYO Surround® for LIBTAYO® (cemiplimab-rwlc) Commercial Copay Program",
  drugDisplayName: "LIBTAYO",
  hideGrantTotal: true,
  possibleDrugs: [{ apDrugDisplayName: "LIBTAYO® (cemiplimab-rwlc)" }],
  lookbackRelevanceTime: "--",
  maxFpl: "--"
};

const regeneronPapOverrides = {
  requiredDiagnosis: "aNSCLC, aBCC, aCSCC, High-Risk CSCC",
  assetId: "US.LIB.26.01.0097 02/26",
  APProvider: "Regeneron",
  displayName: "LIBTAYO Surround® for LIBTAYO® (cemiplimab-rwlc) Patient Assistance Program",
  drugDisplayName: "LIBTAYO",
  hideGrantTotal: true,
  possibleDrugs: [{ apDrugDisplayName: "LIBTAYO® (cemiplimab-rwlc)" }],
  lookbackRelevanceTime: "--",
  maxFpl: "--"
};

const lillyOverrides = {
  hideGrantTotal: true
};

export const FIELD_VALUE_OVERRIDES = {
  [TEMPLATE_NAMES.REGENERON_COPAY]: regeneronCopayOverrides,
  [TEMPLATE_NAMES.REGENERON_PAP]: regeneronPapOverrides,
  [TEMPLATE_NAMES.BMS_COPAY]: {
    acceptedEnrolls: [],
    patientConsentRequired: null,
    APProvider: BMS_SHORT_NAME,
    hideValidThrough: true,
    hideGrantTotal: true
  },
  [TEMPLATE_NAMES.LILLY_COPAY]: lillyOverrides,
  [TEMPLATE_NAMES.LILLY_COPAY_SIGNATURE]: lillyOverrides,
  [TEMPLATE_NAMES.TEVA_COPAY]: {
    appendDrugToDisplayName: true
  }
};

/**
 * PID-based field overrides for programs that share a template but need different display values.
 * Applied after template-based overrides.
 */
const PID_FIELD_OVERRIDES = {
  [TEVA_COPAY_CARD_PID]: {
    displayName: "AUSTEDO XR® (deutetrabenazine) Teva Neuroscience Copay Card"
  },
  [TEVA_SUPPORT_PROGRAM_PID]: {
    displayName: "AUSTEDO XR® (deutetrabenazine) Teva Total Support"
  }
};

export const applyFieldOverrides = (templateName, original, pid) => {
  const templateOverrides = FIELD_VALUE_OVERRIDES[templateName] || {};
  const pidOverrides = PID_FIELD_OVERRIDES[pid || original?.pid || original?.assistanceProgramPid] || {};
  return {
    ...original,
    ...templateOverrides,
    ...pidOverrides
  };
};
